import { wgResource, wgResourceType } from "./wgResource.js";
import { wgBuffer } from "./wgBuffer.js";

const wgVertexBufferType = {
    wgInvalid: -1,
    wgArrayBuffer: 0,
    wgElementsBuffer: 1,
};

const wgIndexType = {
    wgUnsignedByte: 0,
    wgUnsignedShort: 1,
    wgUnsignedInt: 2 // requires gl.getExtension('OES_element_index_uint')
}

class wgVertexBuffer extends wgResource {
    constructor(name, context) {
        super(name, context, wgResourceType.wgVertexBuffer);
        this.vertexBufferType_ = wgVertexBufferType.wgInvalid;
        this.vertexBuffer_ = null;
        this.indexBuffer_ = null; // only used in case of elements indexed buffer.
        this.indexType_ = wgIndexType.wgUnsignedShort;
        this.indexGlType_ = -1;
        this.hasIndexBuffer_ = false;
        this.vertexArrayObject_ = null;
        this.vertexAtributes_ = [];
        this.vertexCount_ = 0;
    }

    deleteVertexBuffer() {
        if (this.vertexArrayObject_ !== null) {
            this.unbind();
            this.context_.gl().deleteVertexArray(this.vertexArrayObject_);
            this.vertexBufferType_ = wgVertexBufferType.wgInvalid;
            this.vertexArrayObject_ = null;
            this.vertexBuffer_.deleteBuffer();
            this.vertexBuffer_ = null;
            this.vertexSizeInBytes_ = 0;
            this.vertexCount_ = 0;
        }
    }

    getVertexArrayObject() {
        return this.vertexArrayObject_;
    }
    getVertexBuffertype() {
        return this.vertexBufferType_;
    }

    getIndexType() {
        return this.indexType_;
    }

    getGlIndexType() {
        return this.indexGlType_;
    }

    bind() {
        this.context_.gl().bindVertexArray(this.vertexArrayObject_);
    }

    unbind() {
        this.context_.gl().bindVertexArray(null);
    }

    draw(primitiveType) {
        const gl = this.getContext().gl();
        this.bind();
        if (!this.hasIndexBuffer_) {
            gl.drawArrays(primitiveType, 0, this.vertexCount_);
        }
        else {
            this.indexBuffer_.bind();
            gl.drawElements(primitiveType, this.vertexCount_, this.getGlIndexType(), 0);
            this.indexBuffer_.unbind();
        }
        this.unbind();
    }

    createVertexBuffer(usage, vertexLayouts, numberOfVertices, vertexSize, vertexData) {
        if (vertexLayouts.length === 0) {
            let errorMessage = "wgVertexcreateVertexBuffer (createVertexBuffer) : Invalid vertexLayouts";
            throw new Error(errorMessage);
        }

        let gl = this.context_.gl(); // for convenience
        this.vertexBuffer_ = new wgBuffer("vertexBuffer", this.context_);
        this.vertexBuffer_.createBuffer(gl.ARRAY_BUFFER, usage, numberOfVertices, vertexSize, vertexData);
        this.vertexBufferType_ = wgVertexBufferType.wgArrayBuffer;
        this.vertexArrayObject_ = gl.createVertexArray();

        if (vertexData === null || vertexData === undefined) {
            this.vertexCount_ = 0;
        }
        else {
            this.vertexCount_ = numberOfVertices;
        }

        this.vertexBuffer_.bind();
        this.bind();

        for (let i = 0; i < vertexLayouts.length; i++) {
            let va = vertexLayouts[i];
            gl.enableVertexAttribArray(va.attLocation_);
            gl.vertexAttribPointer(
                va.attLocation_,
                va.attDimension_,
                va.attDataType_,
                false,
                va.getStride(),
                va.getOffset()
            );
        }

        this.unbind();
        this.vertexBuffer_.unbind();
    }

    createElementsVertexBuffer(usage, vertexLayouts, numberOfVertices, vertexSize, indexType, indexData, vertexData) {
        if (vertexLayouts.length === 0) {
            let errorMessage = "wgVertexcreateVertexBuffer (createVertexBuffer) : Invalid vertexLayouts";
            throw new Error(errorMessage);
        }

        let gl = this.context_.gl(); // for convenience
        this.indexType_ = indexType;
        let indexBytesSize = 2;

        if (this.indexType_ == wgIndexType.wgUnsignedByte) {
            this.indexGlType_ = gl.UNSIGNED_BYTE;
            indexBytesSize = 1;
        }
        else if (this.indexType_ == wgIndexType.wgUnsignedShort) {
            this.indexGlType_ = gl.UNSIGNED_SHORT;
            indexBytesSize = 2;
        }
        else if (this.indexType_ == wgIndexType.wgUnsignedInt) {
            this.indexGlType_ = gl.UNSIGNED_INT;
            indexBytesSize = 4;
        }
        else {
            console.log("Error! unknown index type.");
            return;
        }

        this.vertexCount_ = indexData.length;
        this.hasIndexBuffer_ = true;
        this.indexBuffer_ = new wgBuffer("elementsIndexBuffer", this.context_);
        this.indexBuffer_.createBuffer(gl.ELEMENT_ARRAY_BUFFER, usage, indexData.length, indexBytesSize, indexData);

        this.vertexBuffer_ = new wgBuffer("elementsVertexBuffer", this.context_);
        this.vertexBuffer_.createBuffer(gl.ARRAY_BUFFER, usage, numberOfVertices, vertexSize, vertexData);
        this.vertexBufferType_ = wgVertexBufferType.wgElementsBuffer;
        this.vertexArrayObject_ = gl.createVertexArray();
        this.vertexBuffer_.bind();
        this.bind();

        for (let i = 0; i < vertexLayouts.length; i++) {
            let va = vertexLayouts[i];
            gl.enableVertexAttribArray(va.attLocation_);
            gl.vertexAttribPointer(
                va.attLocation_,
                va.attDimension_,
                va.attDataType_,
                false,
                va.getStride(),
                va.getOffset()
            );
        }
        this.unbind();
        this.vertexBuffer_.unbind();
    }

    updateVertexBufferData(offset, vertexData) {
        this.vertexCount_ = vertexData.length / (vertexData.getVertexByteSize * Float32Array.BYTES_PER_ELEMENT);
        this.vertexBuffer_.updateBuffer(offset, vertexData);
    }

    updateIndexBufferData(offset, indexData) {
        this.vertexCount_ = vertexData.length;
        this.indexBuffer_.updateBuffer(offset, indexData);
    }
}

export { wgVertexBuffer, wgVertexBufferType, wgIndexType };
